import { always, assoc, evolve, ifElse, map, pick, pipe, when, propEq, dissoc, omit } from 'ramda';
import get from 'lodash/get';
import reject from 'lodash/fp/reject';
import uuidv4 from 'uuid/v4';
import { getPaymentTypeLabel } from './payment/reducer/payments/constants';
import * as orderSelectors from './order/selectors';
import * as promoSelectors from './promo/selectors';
import * as tippingSelectors from './tipping/selectors';
import { getCustomerId } from './customer/selectors';
import { PAYMENT_TYPE } from '../main/payment/constants';
import { getPayments } from './payment/reducer/payments/selectors';
import { getHasPromo, getValidatedOrder } from './validated/selectors';
import { none } from '../common/helpers/functional';
import { UNASSIGNED_BARBER, ITEM_TYPE } from './constants';
import { getById as getBarberById } from '../main/barbers/selector';
import { getActiveShop } from '../auth/selectors';

const getItemsForValidation = (orderItems, customerId) => {
  const pickKeys = pick(['id', 'quantity', 'type', 'barberId', 'amount', 'description', 'code', 'createdAt']);

  const formatGiftCard = ifElse(
    item => !!item.createdAt,
    omit(['createdAt', 'quantity', 'description', 'code']),
    dissoc('id'),
  );

  const mapItems = pipe(
    pickKeys,
    when(propEq('barberId', UNASSIGNED_BARBER), dissoc('barberId')),
    when(propEq('type', ITEM_TYPE.giftCard), formatGiftCard),
    assoc('customerId', customerId)
  );
  return map(mapItems, orderItems);
};

export const getOrderForValidation = (state) => {
  const orderItems = orderSelectors.getAllOrderItems(state);
  const customerId = getCustomerId(state);
  const items = getItemsForValidation(orderItems, customerId);
  const discounts = orderSelectors.getOrderDiscounts(state);
  const promoCode = promoSelectors.getPromoCode(state);
  const payments = getPayments(state);
  const tips = tippingSelectors.getTips(state);
  return { items, discounts, promoCode, payments, tips };
};

export const emptyCashPayment = () => ({
  id: uuidv4(),
  type: PAYMENT_TYPE.cash,
  label: getPaymentTypeLabel(PAYMENT_TYPE.cash),
  amount: 0,
  empty: true,
});

const isCashPayment = payment => payment.type === PAYMENT_TYPE.cash;

const removeEmptyPayments = order => evolve({
  payments: reject(payment => !payment.amount && isCashPayment(payment) && none({ paymentId: payment.id }, order.tips)),
}, order);

const cleanUpPayments = when(x => x.payments.length !== 1, removeEmptyPayments);

const removeCashTips = order => evolve({
  tips: reject(isCashPayment),
}, order);

const populateItemsWithBarbers = state => order => evolve({
  items: map((item) => {
    const barber = getBarberById(state, item.barberId);
    return {
      ...item,
      barber: barber ? pick(['id', 'firstName'], barber) : null,
    };
  }),
}, order);

export const getOrderForCheckout = pipe(getOrderForValidation, cleanUpPayments);
export const getOrderForWebreaderSale = pipe(getOrderForCheckout, removeCashTips);
export const getOrderForListeningTipping = state => pipe(getValidatedOrder, populateItemsWithBarbers(state))(state);

export const getAppliedPromoCode = ifElse(
  getHasPromo,
  promoSelectors.getPromoCode,
  always(''),
);

export const isCashOutTipsShortcutEnabled = state => get(getActiveShop(state), 'settings.cashOutTipsShortcutEnabled');
export const paymentDepositsEnabled = state => get(getActiveShop(state), 'settings.paymentDepositsEnabled');
